// "Clicks & orders" tab: the click-flow story in reading order — the flows in detail
// (Received/Sent cards), what they net to (Your click balance + rank), and what is
// suppressing them (Sale filter impact — shown only to retailers with the filter ON;
// filter-off retailers must not be promoted into asking for it). The balance/filter
// sections render nothing while the badge-fairness rollout keeps this retailer dark,
// so the tab is useful to every retailer from day one. Cards follow the page date
// picker; the fairness sections carry their own fixed "Last 30 days" window (chips
// label this).
import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Grid,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import { dashboardAPI } from '../../services/dashboard-api';
import { formatHours } from '../../utils/formatters';
import BadgeFairnessSection, { TopSpotSteps } from './BadgeFairnessSection';
import MatchExceptionsSection from './MatchExceptionsSection';

const CohortLine = ({ value, sampleClicks, threshold = 20, tooltipText }) => {
  if (sampleClicks < threshold || value == null) return null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
      <Typography variant="caption" color="text.secondary">
        Partners avg: {value}
      </Typography>
      {tooltipText && (
        <Tooltip title={tooltipText} arrow>
          <Box component="span" sx={{ display: 'flex', alignItems: 'center', cursor: 'help', color: 'text.disabled', fontSize: 14 }}>ⓘ</Box>
        </Tooltip>
      )}
    </Box>
  );
};

const MetricItem = ({ label, value, cohort, cohortSample, cohortTooltip }) => (
  <Box sx={{ py: 0.75 }}>
    <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: 600 }}>{value}</Typography>
    {cohort !== undefined && (
      <CohortLine value={cohort} sampleClicks={cohortSample} tooltipText={cohortTooltip} />
    )}
  </Box>
);

const DirectionBlock = ({ title, data, cohortTooltipConv, cohortTooltipTime, footer, extraRows, topSpot }) => {
  if (!data) return null;
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          {title}
        </Typography>
        <MetricItem label="Clicks" value={(data.clicks || 0).toLocaleString()} />
        {data.contested_clicks != null && (
          <Box sx={{ py: 0.75 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              Competed for the top spot
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {`${(data.contested_clicks || 0).toLocaleString()} of ${(data.clicks || 0).toLocaleString()} clicks`}
            </Typography>
            {/* Fairness language — renders only when the badge-fairness section is
                enabled for this retailer (same gate, one rollout switch). */}
            {topSpot && (
              <Tooltip
                title={
                  <Box sx={{ maxWidth: 340 }}>
                    <TopSpotSteps filterOn={topSpot.filterOn} />
                  </Box>
                }
                arrow
                enterTouchDelay={0}
                leaveTouchDelay={5000}
              >
                <Typography
                  variant="caption"
                  sx={{
                    textDecoration: 'underline dotted',
                    textUnderlineOffset: '3px',
                    color: 'text.secondary',
                    cursor: 'help',
                    display: 'inline-block',
                    mt: 0.25,
                  }}
                >
                  How the top spot works
                </Typography>
              </Tooltip>
            )}
          </Box>
        )}
        {(extraRows || []).map((row) => (
          <MetricItem key={row.label} label={row.label} value={row.value} />
        ))}
        {/* Click-anchored: of the clicks above, how many converted. Deliberately NOT
            the same number as "Orders acquired" (order-date-anchored, matches
            commission reporting) — a boundary order from an earlier click counts
            there but not here. */}
        <MetricItem label="Orders from these clicks" value={(data.orders || 0).toLocaleString()} />
        <MetricItem
          label="Conversion rate"
          value={data.conversion_pct != null ? `${data.conversion_pct}%` : '—'}
          cohort={data.cohort_conversion_pct != null ? `${data.cohort_conversion_pct}%` : null}
          cohortSample={data.cohort_sample_clicks || 0}
          cohortTooltip={cohortTooltipConv}
        />
        <MetricItem
          label="Typical click to order time"
          value={formatHours(data.p50_conversion_hours)}
          cohort={data.cohort_p50_conversion_hours != null ? formatHours(data.cohort_p50_conversion_hours) : null}
          cohortSample={data.cohort_sample_clicks || 0}
          cohortTooltip={cohortTooltipTime}
        />
        {footer && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {footer}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

// Filter completeness rows for the direction cards (spec v2.2 decision 5): only when
// the badge-fairness section is enabled AND a flag is on — same payload as the section,
// so the numbers can never disagree with the story above them. Estimates, 30d window.
const nl = (v) => (v == null ? null : v.toLocaleString('nl-NL'));
const badgeCardRows = (badge) => {
  const filterOn =
    badge?.enabled &&
    (badge.sale_filter?.hide_discounted_matches_on_full_price ||
      badge.sale_filter?.hide_deeper_discounts_when_on_sale);
  if (!filterOn || !badge.impact) return { received: undefined, sent: undefined };
  // Mirrored formats: "~<times> times (≈ <clicks> clicks, ≈ <outcome> missed)".
  const received =
    badge.impact.hidden_views != null
      ? [
          {
            label: 'Your sale filter hid you (30d)',
            value: `~${nl(badge.impact.hidden_views)} times (≈ ${nl(badge.impact.est_missed_clicks)} clicks${
              badge.impact.est_missed_orders != null ? `, ≈ ${nl(badge.impact.est_missed_orders)} orders` : ''
            } missed)`,
          },
        ]
      : undefined;
  const sent =
    badge.impact.own_pages_missed_clicks != null
      ? [
          {
            label: 'Your sale filter hid partners (30d)',
            value: `${
              badge.protection?.protected_calls != null ? `~${nl(badge.protection.protected_calls)} times (` : '('
            }≈ ${nl(badge.impact.own_pages_missed_clicks)} clicks, ≈ €${nl(
              badge.impact.own_pages_missed_commission_eur
            )} commission missed)`,
          },
        ]
      : undefined;
  return { received, sent };
};

// The "How the top spot works" explainer next to the contested rows is fairness
// language — it renders only when the badge-fairness section is enabled (same gate).
const badgeTopSpot = (badge) => {
  if (!badge?.enabled) return null;
  return {
    filterOn: Boolean(
      badge.sale_filter?.hide_discounted_matches_on_full_price ||
        badge.sale_filter?.hide_deeper_discounts_when_on_sale
    ),
  };
};

const ClicksOrdersTab = ({ startDate, endDate, myRetailerId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  // Badge-fairness payload: fetched once (fixed 30d window, not picker-driven); feeds
  // the balance/filter sections AND the filter rows on the direction cards, so the
  // numbers everywhere on this tab come from one source and can never disagree.
  // undefined = still loading (skeleton), null = failed/disabled (render nothing).
  const [badge, setBadge] = useState(undefined);

  useEffect(() => {
    let cancelled = false;
    dashboardAPI
      .getBadgeFairness()
      .then((res) => {
        if (!cancelled) setBadge(res?.success ? res.data : null);
      })
      .catch(() => {
        // Additive feature — any failure renders nothing rather than an error.
        if (!cancelled) setBadge(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Match-exception rules (owner + affected side), same loading contract as badge:
  // undefined = loading, null = failed/disabled -> renders nothing.
  const [exceptions, setExceptions] = useState(undefined);

  useEffect(() => {
    let cancelled = false;
    dashboardAPI
      .getMatchExceptions()
      .then((res) => {
        if (!cancelled) setExceptions(res?.success ? res.data : null);
      })
      .catch(() => {
        if (!cancelled) setExceptions(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const loadTraffic = useCallback(async () => {
    try {
      setLoading(true);
      setError(false);
      const response = await dashboardAPI.getTrafficStats({
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
        my_retailer_id: myRetailerId || undefined,
      });
      if (response.success && response.data) {
        setData(response.data);
      } else {
        setData(null);
        setError(true);
      }
    } catch {
      setData(null);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, myRetailerId]);

  useEffect(() => {
    loadTraffic();
  }, [loadTraffic]);

  if (loading) {
    return (
      <Box>
        <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 1, mb: 2 }} />
        <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 1 }} />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Alert severity="info" variant="outlined">
        Click data is currently unavailable.
      </Alert>
    );
  }

  return (
    <Box>
      {/* 1. The flows in detail */}
      {(data.received || data.sent) && (
        <Grid container spacing={{ xs: 1.5, sm: 2 }} mb={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <DirectionBlock
              title="Received from partners"
              data={data.received}
              extraRows={badgeCardRows(badge).received}
              topSpot={badgeTopSpot(badge)}
              cohortTooltipConv="Average conversion rate of these partner retailers when sending to other retailers — not you. Are you converting their traffic above or below their norm?"
              cohortTooltipTime="Median time from click to order for these partners' traffic at other retailers."
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <DirectionBlock
              title="Sent to partners"
              data={data.sent}
              extraRows={badgeCardRows(badge).sent}
              footer="These orders were sales you could not fulfil. Your sizes were sold out. A partner completed the sale and you earned the commission."
            />
          </Grid>
        </Grid>
      )}

      {/* 2. What they net to (click balance + rank) and 3. what suppresses them
          (sale filter impact) — both inside BadgeFairnessSection, dark-gated.
          Skeleton while its payload loads (the endpoint aggregates ~8 queries and
          lands after the cards) so the sections don't pop in with a layout shift. */}
      {badge === undefined ? (
        <Skeleton variant="rectangular" height={160} sx={{ borderRadius: 1 }} />
      ) : (
        <BadgeFairnessSection data={badge} exceptions={exceptions} />
      )}

      {/* 4. Partner rules (exception engine): rules this retailer owns and rules that
          affect them, each with its cost. Dark-gated; renders nothing while dark. */}
      {exceptions !== undefined && <MatchExceptionsSection data={exceptions} />}
    </Box>
  );
};

export default ClicksOrdersTab;
